<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>
          {{ 'knittingDashboard.title' | translateWF: 'Knitting Dashboard' }}
        </h1>
      </div>
    </div>

    <div class="row-end-center">
      <p-selectbutton [(ngModel)]="viewMode" [options]="viewModeOptions" optionLabel="label" optionValue="value">
        <ng-template let-item #item>
          <i [class]="item.icon"></i>
          <span class="ml-2">{{ item.label }}</span>
        </ng-template>
      </p-selectbutton>
    </div>

    <div class="grid-12-lg">
      <div class="col" [ngClass]="showOperators ? 'span-8' : 'span-12'">
        <p-panel [header]="'Filters' | translateWF: 'Filters'">
          <p-card>
            <div class="grid-auto-4">
              <div class="form-box">
                <label for="">
                  {{ 'knittingDashboard.needles' | translateWF: 'Needles' }}
                </label>
                <p-multiSelect
                  inputId="needles-select"
                  [(ngModel)]="selectedNeedles"
                  [options]="uniqueNeedleOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="'knittingDashboard.selectNeedles' | translateWF: 'Select Needles'"
                  styleClass="w-full"
                />
              </div>
              <div class="form-box">
                <label for=""> Machine Type </label>
                <p-multiSelect
                  inputId="machine-type-select"
                  [(ngModel)]="machineTypeValues"
                  [options]="machineTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  styleClass="w-full"
                />
              </div>
              <div class="form-box">
                <label for="machineIdFilter">
                  {{ 'knittingDashboard.machineNumber' | translateWF: 'Machine Number' }}
                </label>
                <input
                  id="machineIdFilter"
                  type="text"
                  pInputText
                  [(ngModel)]="searchMachineId"
                  (ngModelChange)="onSearchFiltersChange()"
                  [placeholder]="'knittingDashboard.searchMachine' | translateWF: 'Search by machine ID'"
                  styleClass="w-full"
                />
              </div>
              @if (searchMachineId) {
                <div class="flex items-center">
                  <p-button icon="pi pi-times" [label]="'knittingDashboard.clearFilters' | translateWF: 'Clear Filters'" severity="secondary" (click)="clearSearchFilters()" />
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <div class="grid-auto-3">
          @if (viewMode === 'grid') {
            @for (machine of filteredKnittingMachines(); track machine.assetId) {
              @defer (on viewport; prefetch on idle) {
                @let changed = knittingMachinedChanged(machine.assetId);
                <p-card
                  class="machine__card p-shadow-3 p-p-4"
                  [style]="{
                    '--p-card-background': getStatusBgColor(machine.status),
                  }"
                  [ngClass]="'p-shadow-3 p-p-4 ' + getStatusBgClass(machine.status)"
                >
                  <!-- Header: Asset ID + Status -->
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-xl font-semibold">{{ machine.assetId }}</h3>
                    <p-select
                      [options]="statusOptions"
                      [(ngModel)]="machine.status"
                      optionLabel="label"
                      optionValue="value"
                      [filter]="true"
                      filterBy="label"
                      [showClear]="true"
                      placeholder="Select status"
                      class="w-40"
                      [disabled]="blockAutoCompleteInputs()"
                    >
                      <ng-template #selectedItem let-opt>
                        <div class="flex items-center gap-2">
                          <i class="pi" [ngClass]="opt.icon" [style.color]="opt.color"></i>
                          <span>{{ opt.label }}</span>
                        </div>
                      </ng-template>
                      <ng-template let-opt #item>
                        <div class="flex items-center gap-2">
                          <i class="pi" [ngClass]="opt.icon" [style.color]="opt.color"></i>
                          <span>{{ opt.label }}</span>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                  <h4>{{ getAsset(machine.assetId)?.assetTypeName }}</h4>
                  <h2>{{ getAsset(machine.assetId)?.description }}</h2>

                  <!-- Knitting Machine Details: 2-column grid -->
                  @if (getAsset(machine.assetId)?.knittingMachineDetails) {
                    <div class="grid-auto-2 gap-2 my-4">
                      <div class="p-field">
                        <label class="block mb-1 text-sm font-medium">
                          {{ 'knittingDashboard.cylinderNeedles' | translateWF: 'Cylinder Needles' }}
                        </label>
                        <p-inputnumber
                          [disabled]="blockSection()"
                          [inputStyle]="{ 'max-width': '7rem' }"
                          [(ngModel)]="getAsset(machine.assetId)!.knittingMachineDetails!.cylinderNeedles"
                        ></p-inputnumber>
                      </div>
                      @if (isDoubleKnittingMachine(getAsset(machine.assetId)!)) {
                        <div class="p-field">
                          <label class="block mb-1 text-sm font-medium">
                            {{ 'knittingDashboard.dialNeedles' | translateWF: 'Dial Needles' }}
                          </label>
                          <p-inputnumber
                            [disabled]="blockSection()"
                            [inputStyle]="{ 'max-width': '7rem' }"
                            [(ngModel)]="getAsset(machine.assetId)!.knittingMachineDetails!.dialNeedles"
                          ></p-inputnumber>
                        </div>
                      }
                      <div class="p-field">
                        <label class="block mb-1 text-sm font-medium">
                          {{ 'knittingDashboard.diameter' | translateWF: 'Diameter (in)' }}
                        </label>
                        <p-inputnumber
                          [disabled]="blockSection()"
                          [inputStyle]="{ 'max-width': '7rem' }"
                          [(ngModel)]="getAsset(machine.assetId)!.knittingMachineDetails!.diameterInches"
                        ></p-inputnumber>
                      </div>
                      <div class="p-field">
                        <label class="block mb-1 text-sm font-medium">
                          {{ 'knittingDashboard.gauge' | translateWF: 'Gauge' }}
                        </label>
                        <p-inputnumber
                          [disabled]="blockSection()"
                          [inputStyle]="{ 'max-width': '7rem' }"
                          [(ngModel)]="getAsset(machine.assetId)!.knittingMachineDetails!.gauge"
                        ></p-inputnumber>
                      </div>
                      <div class="p-field">
                        <label class="block mb-1 text-sm font-medium">
                          {{ 'knittingDashboard.feeders' | translateWF: 'Feeders' }}
                        </label>
                        <p-inputnumber
                          [disabled]="blockSection()"
                          [inputStyle]="{ 'max-width': '7rem' }"
                          [(ngModel)]="getAsset(machine.assetId)!.knittingMachineDetails!.feeders"
                        ></p-inputnumber>
                      </div>
                    </div>
                  }

                  <div class="grid-auto-2 mt-4 gap-4">
                    <p-select
                      [options]="recordKnittingOrders[machine.assetId]"
                      [(ngModel)]="machine.knittingOrderReferenceInProduction"
                      optionLabel="knittingOrderNumber"
                      optionValue="id"
                      (onClick)="getKnittingOrdersByAssetId(machine.assetId)"
                      [placeholder]="'knittingDashboard.selectAnOrder' | translateWF: 'Select an order'"
                      class="w-full"
                      [disabled]="blockAutoCompleteInputs()"
                    >
                    </p-select>
                    <p-select
                      [disabled]="blockAutoCompleteInputs()"
                      [options]="fabricCategories"
                      [(ngModel)]="machine.configuredForFabricCode"
                      optionLabel="localizedName"
                      optionValue="id"
                      [placeholder]="'knittingDashboard.displayName' | translateWF: 'Select a fabric code'"
                      class="w-full"
                    >
                    </p-select>
                  </div>

                  @if (showOperators) {
                    @if (machine.currentOperatorUserName != null) {
                      <div class="operators__list mt__1">
                        <div class="operator__card">
                          <p-avatar [label]="machine.currentOperatorUserName[0]" />
                          {{ machine.currentOperatorUserName }}
                          <p-button [disabled]="blockSection()" severity="secondary" icon="pi pi-trash" (click)="removeMachineOperator($index, null)" />
                        </div>
                      </div>
                    } @else {
                      <div class="drop__wrapper mt__1" pDroppable (onDrop)="drop($index)"></div>
                    }
                  }

                  @if (changed) {
                    <div class="flex justify-end space-x-2 mt-2">
                      <p-button icon="pi pi-save" [label]="'save' | translateWF: 'Save'" severity="success" (click)="saveDetails(machine.assetId)" />
                      <p-button icon="pi pi-refresh" [label]="'reset' | translateWF: 'Reset'" severity="warn" (click)="resetKnittingMachine(machine.assetId)" />
                    </div>
                  }
                </p-card>
              } @placeholder {
                <div class="machine__card--skeleton"></div>
              }
            }
          } @else {
            <div [ngClass]="showOperators ? 'span-8' : 'span-12'">
              <p-panel header="Knitting Machines">
                <p-card data-variant="compact">
                  <p-table [value]="filteredKnittingMachines()">
                    <ng-template #header>
                      <tr>
                        <th>{{ 'knittingDashboard.machineName' | translateWF: 'Machine Name' }}</th>
                        <th>{{ 'knittingDashboard.greigeCode' | translateWF: 'Greige Code' }}</th>
                        <th>{{ 'knittingDashboard.category' | translateWF: 'Category' }}</th>
                        <th>{{ 'knittingDashboard.status' | translateWF: 'Status' }}</th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-machine>
                      <tr>
                        <td>{{ machine.assetId }}</td>
                        <td>Greige-1</td>
                        <td>10 rolls assigned</td>
                        <td>{{ machine.status }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            </div>
          }
        </div>
      </div>
      @if (showOperators) {
        <div class="span-4">
          <p-panel [header]="'knittingDashboard.operators' | translateWF: 'Operators (Drag and Drop)'">
            <div class="operators__list space-y-2">
              @for (operator of operatorsList; track operator.id) {
                <div pDraggable (onDragStart)="dragStart(operator)" (onDragEnd)="dragEnd()" class="operator__card p-shadow-1 p-p-2 flex items-center gap-2">
                  <p-avatar [label]="operator.profile.firstName[0]"></p-avatar>
                  <span>{{ operator.profile.firstName }} {{ operator.profile.lastName }}</span>
                </div>
              }
            </div>
          </p-panel>
        </div>
      }
    </div>
  </div>
</main>
<p-toast position="bottom-right" key="br"></p-toast>
